import api from "$services/api";
import logger from "$services/logger";
import { updateGlobalError } from "$stores/app-context";

const apiEmployees = async (query, callback, apiSettings) => {
    try {
        if (query && !query.match(/^(?:\S*\s){2}/)) {
            const response = await api.get(`/api/employees/all?query=${query}`, null, apiSettings);
            callback(response.employees.map((user) => ({ display: user.fullName, id: user.employeeId })));
        }
    } catch (e) {
        if (api.requestWasCanceled(e)) {
        }
        updateGlobalError("Unable to fetch employees");
        logger.error("Unable to fetch employees", e);
    }
};

const apiActiveUsers = async (query, callback, apiSettings) => {
    try {
        if (query && !query.match(/^(?:\S*\s){2}/)) {
            const response = await api.get(`/api/employees/sharable?query=${query}`, null, apiSettings);
            callback(response.employees.map((user) => ({ display: user.fullName, id: `${user.employeeId}|${user.userId}` })));
        }
    } catch (e) {
        if (api.requestWasCanceled(e)) {
        }
        updateGlobalError("Unable to fetch employees");
        logger.error("Unable to fetch employees", e);
    }
};

export const fetchEmployees = (query, callback) => {
    const abortController = new AbortController();
    apiEmployees(query, callback, { signal: abortController.signal });
    return abortController;
};


export const fetchActiveUsers = (query, callback) => {
    const abortController = new AbortController();
    apiActiveUsers(query, callback, { signal: abortController.signal });
    return abortController;
};

export const extractUserTags = (str) => {

    const tagRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const tags = [];

    let match;
    while ((match = tagRegex.exec(str)) !== null) {
        const userTag = match[1].trim().replace(/\[|\]/g, ""); // Remove square brackets
        const employeeIdUserId = match[2].split("|");
        const employeeId = parseInt(employeeIdUserId[0], 10);
        const userId = employeeIdUserId[1];
        tags.push({ userTag, employeeId, userId });
    }

    // Remove duplicates based on ID
    const uniqueTags = Array.from(new Set(tags.map((tag) => tag.employeeId))).map((id) => tags.find((tag) => tag.employeeId === id));
    return uniqueTags;
};

/**
 * Merges local collaborator list with API search results.
 * Collaborators filtered by query appear first; non-collaborator search results follow, de-duped by userId.
 *
 * @param {string} query - Current typeahead query
 * @param {Array<{userId, employeeId, userName}>} collaborators - Preloaded collaborator list
 * @param {Array<{display, id}>} searchResults - Results from /api/employees/sharable (id = "employeeId|userId")
 * @returns {Array<{display, id, isCollaborator}>}
 */
export const buildMentionSuggestions = (query, collaborators, searchResults) => {
    const q = (query || "").toLowerCase();

    const matchingCollaborators = collaborators
        .filter((c) => c.userName.toLowerCase().includes(q))
        .map((c) => ({
            display: c.userName,
            id: `${c.employeeId}|${c.userId}`,
            isCollaborator: true,
        }));

    const collaboratorUserIds = new Set(collaborators.map((c) => c.userId));

    const nonCollaboratorResults = searchResults
        .filter((r) => {
            const userId = r.id.split("|")[1];
            return !collaboratorUserIds.has(userId);
        })
        .map((r) => ({ ...r, isCollaborator: false }));

    return [...matchingCollaborators, ...nonCollaboratorResults];
};

const escapeHtml = (str) =>
    str.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#039;" }[c]));

export const replaceMentionsWithLinks = (text) => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let result = "";
    let lastIndex = 0;
    let match;
    while ((match = mentionRegex.exec(text)) !== null) {
        result += escapeHtml(text.slice(lastIndex, match.index));
        const employeeId = match[2].split("|")[0];
        result += `<a href='/team/${employeeId}'>${escapeHtml(match[1])}</a>`;
        lastIndex = match.index + match[0].length;
    }
    result += escapeHtml(text.slice(lastIndex));
    return result;
};
